import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Timer } from "lucide-react";
import { SITE, WHATSAPP_DEFAULT_MESSAGE, IMAGES } from "@/config/siteConfig";
import { waLink, trackEvent } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const NAV = [
  { label: "Offres", href: "#offres", testId: "nav-offres" },
  { label: "Services", href: "#services", testId: "nav-services" },
  { label: "Pourquoi nous", href: "#pourquoi", testId: "nav-pourquoi" },
  { label: "FAQ", href: "#faq", testId: "nav-faq" },
  { label: "Contact", href: "#contact", testId: "nav-contact" },
];

export const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-black/5 bg-white/80 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:h-20 md:px-8">
        <a
          href="#accueil"
          onClick={(e) => { e.preventDefault(); scrollToId("accueil"); }}
          className="flex items-center gap-3"
          data-testid="header-logo-link"
          aria-label={`${SITE.companyName} — retour en haut`}
        >
          <span className="flex h-10 items-center overflow-hidden rounded-md bg-black px-2">
            <img src={IMAGES.logo} alt={`Logo ${SITE.companyName}`} className="h-7 w-auto object-contain" />
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigation principale">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={item.testId}
              onClick={(e) => { e.preventDefault(); scrollToId(item.href.slice(1)); }}
              className="text-sm font-medium text-gray-600 transition-colors duration-300 hover:text-black"
            >
              {item.label}
            </a>
          ))}
          <Link
            to="/quiz"
            data-testid="nav-quiz"
            className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-bold text-black transition-colors duration-300 hover:border-jeem-yellow hover:bg-jeem-yellow/20"
          >
            <Timer className="h-4 w-4" /> Test 30 s
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink(WHATSAPP_DEFAULT_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("clic_whatsapp", { source: "header" })}
            data-testid="header-whatsapp-cta"
            className="hidden h-11 items-center gap-2 rounded-full bg-jeem-yellow px-5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-jeem-yellow-dark sm:flex"
          >
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-black/5 bg-white lg:hidden"
            aria-label="Navigation mobile"
          >
            <div className="flex flex-col px-4 py-4">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  data-testid={`mobile-${item.testId}`}
                  onClick={(e) => { e.preventDefault(); setOpen(false); scrollToId(item.href.slice(1)); }}
                  className="border-b border-gray-100 py-3.5 text-base font-medium text-gray-800"
                >
                  {item.label}
                </a>
              ))}
              <Link
                to="/quiz"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-quiz"
                className="border-b border-gray-100 py-3.5 text-base font-bold text-black"
              >
                Test 30 s — Payez-vous trop cher ?
              </Link>
              <a
                href={waLink(WHATSAPP_DEFAULT_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("clic_whatsapp", { source: "menu_mobile" })}
                data-testid="mobile-menu-whatsapp-cta"
                className="mt-4 flex h-12 items-center justify-center gap-2 rounded-full bg-jeem-yellow text-base font-semibold text-black"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Parler à un conseiller
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
